/**
 * ADA Fix: Cart Notes Focus Trap Issue
 * 
 * Issue: Keyboard focus is trapped inside the "Add order note" textarea in the cart drawer.
 * Users cannot navigate using TAB or SHIFT+TAB to move forward or backward.
 * When ESC is pressed, focus incorrectly returns to "Add order note" button.
 * 
 * Solution: Disable focus trap for the cart notes collapsible panel to allow 
 * normal keyboard navigation and proper ESC key behavior.
 */

(function() {
  'use strict';

  function fixCartNotesFocusTrap() {
    const cartNotesPanel = document.getElementById('CartNotesPanel');
    const cartNotesTextarea = document.getElementById('CartNotes');
    
    if (!cartNotesPanel) {
      return;
    }

    // Override the focusTrapOptions to disable the focus trap
    // This allows normal TAB navigation within the cart drawer
    Object.defineProperty(cartNotesPanel, 'focusTrapOptions', {
      get: function() {
        return {
          // Prevent focus trap from activating
          onActivate: () => {},
          onDeactivate: () => {},
          // Allow clicks outside without trapping focus
          clickOutsideDeactivates: true,
          allowOutsideClick: true,
          // Don't return focus when deactivating
          returnFocusOnDeactivate: false,
          // Prevent focus from being trapped
          escapeDeactivates: true,
          // Allow user to tab through normally
          preventScroll: true,
          // Don't set initial focus
          setReturnFocus: false,
          initialFocus: false,
          fallbackFocus: false
        };
      },
      configurable: true
    });

    // Deactivate any existing focus trap
    if (cartNotesPanel._focusTrap) {
      try {
        cartNotesPanel._focusTrap.deactivate();
      } catch (e) {
        console.warn('Could not deactivate focus trap:', e);
      }
      // Reset the focus trap so it gets recreated with new options
      cartNotesPanel._focusTrap = null;
    }

    // Handle ESC key properly - close the panel instead of returning focus to button
    if (cartNotesTextarea) {
      cartNotesTextarea.addEventListener('keydown', function(event) {
        if (event.key === 'Escape' || event.keyCode === 27) {
          event.preventDefault();
          event.stopPropagation();
          
          // Close the collapsible panel
          if (cartNotesPanel.open) {
            cartNotesPanel.open = false;
          }
          
          // Move focus to next logical element (checkout button)
          // instead of back to the "Add order note" button
          const checkoutButton = document.querySelector('.cart-drawer__checkout-button')?.closest('button');
          if (checkoutButton) {
            checkoutButton.focus();
          }
        }
      });
    }
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fixCartNotesFocusTrap);
  } else {
    fixCartNotesFocusTrap();
  }

  // Also run when the cart drawer is opened
  document.addEventListener('expandable-html-element:open', function(event) {
    if (event.target && event.target.id === 'CartDrawer') {
      // Small delay to ensure the panel is rendered
      setTimeout(fixCartNotesFocusTrap, 100);
    }
  });

  // Listen for dynamic content changes (e.g., cart updates)
  if (window.MutationObserver) {
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.addedNodes.length) {
          mutation.addedNodes.forEach(function(node) {
            if (node.id === 'CartNotesPanel' || 
                (node.querySelector && node.querySelector('#CartNotesPanel'))) {
              fixCartNotesFocusTrap();
            }
          });
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

})();
